import { executeDefiOperation } from "../../shared/executor";
import { getLogger } from "../../shared/logger";
import { parseChainIdToExpand, parseOptionalChainIdToExpand } from "../chains/chain-service";
import { normalizeBridgeResponse, normalizeSwapResponse, transformApiMetadataV2Response, transformBridgeQuoteResponse, transformBridgeStatusResponse, transformLendBorrowWithdrawResponse, transformLendborrowDepositResponse, transformLendborrowPoolResponse, transformLendborrowPoolsResponse, transformSwapQuoteResponse, transformUserPositionsResponse, } from "./mappings";
import { _getTransactions } from "./transactions";
import { validateApiMetadataV2Params, validateBridgeQuoteParams, validateBridgeStatusParams, validateBridgeTransactionParams, validateLendborrowDepositParams, validateLendborrowPoolParams, validateLendborrowPoolsParams, validateLendborrowWithdrawParams, validateSwapQuoteParams, validateSwapTransactionParams, validateUserPositionsParams, } from "./validate";
// ========== AUXILIARY FUNCTIONS ==========
async function _getBridgeStatus(defiApi, logger, request) {
    validateBridgeStatusParams({
        transactionHash: request.transactionHash,
        bridgeId: request.bridgeId,
    });
    const parsedSrcChainId = parseOptionalChainIdToExpand(request.srcChainId, "srcChainId");
    const sdkRequest = {
        transactionHash: request.transactionHash,
        bridgeId: request.bridgeId,
        srcChainId: parsedSrcChainId,
        srcChainSymbol: request.srcChainSymbol,
    };
    const raw = await defiApi.getBridgeStatus(sdkRequest);
    return transformBridgeStatusResponse(raw, request.bridgeId, logger);
}
async function _lendBorrowDeposit(defiApi, logger, request) {
    const payload = request.lendborrowDepositRequestPayload;
    if (!payload) {
        throw new Error("lendborrowDepositRequestPayload is required");
    }
    validateLendborrowDepositParams({
        asset: payload.asset,
        amount: payload.amount,
        onBehalfOf: payload.onBehalfOf,
        from: payload.from,
    });
    const raw = await defiApi.lendborrowDeposit(request);
    return transformLendborrowDepositResponse(raw, logger);
}
async function _buildSwapQuote(defiApi, logger, request) {
    const { chainId, path, amountIn, chainSymbol, excludedDexes, poolFees } = request;
    validateSwapQuoteParams({ chainId, path, amountIn });
    const parsedChainId = parseChainIdToExpand(chainId, "chainId");
    const raw = await defiApi.buildSwapQuote({
        chainId: parsedChainId,
        path,
        amountIn,
        chainSymbol,
        excludedDexes,
        poolFees,
    });
    return transformSwapQuoteResponse(raw, chainId, logger);
}
async function _buildBridgeQuote(defiApi, logger, request) {
    const { dstChainId, srcTokenSymbol, dstTokenSymbol, amountIn, srcTokenAddress, dstTokenAddress } = request;
    validateBridgeQuoteParams({
        dstChainId,
        srcTokenSymbol,
        dstTokenSymbol,
        amountIn,
        srcTokenAddress,
        dstTokenAddress,
    });
    const parsedDst = parseChainIdToExpand(dstChainId, "dstChainId");
    const parsedSrc = parseOptionalChainIdToExpand(request.srcChainId, "srcChainId");
    const excluded = request.excludedBridges;
    const normalizedExcluded = Array.isArray(excluded) ? excluded.join(",") : excluded;
    const sdkReq = {
        dstChainId: parsedDst,
        srcTokenSymbol,
        dstTokenSymbol,
        srcTokenAddress,
        dstTokenAddress,
        amountIn,
        srcChainId: parsedSrc,
        srcChainSymbol: request.srcChainSymbol,
        excludedBridges: normalizedExcluded,
    };
    const raw = await defiApi.buildBridgeQuote(sdkReq);
    return transformBridgeQuoteResponse(raw, parsedSrc ?? "", parsedDst, logger);
}
async function _buildSwapTransactionWithApproval(defiApi, _logger, request) {
    let payload;
    if (request.dexSwapwithapprovalRequestPayload !== undefined) {
        payload = request;
    }
    else {
        const r = request;
        payload = {
            dexSwapwithapprovalRequestPayload: {
                dexId: r.dexId,
                path: r.path,
                amountIn: r.amountIn,
                amountOutMin: r.amountOutMin,
                to: r.to,
                deadline: r.deadline,
                from: r.from,
                slippage: r.slippage,
                gas: r.gas,
                gasPriority: r.gasPriority,
                enableFee: r.enableFee,
                poolFees: r.poolFees,
            },
        };
    }
    const flat = payload.dexSwapwithapprovalRequestPayload;
    validateSwapTransactionParams({
        dexId: flat.dexId,
        path: flat.path,
        amountIn: flat.amountIn,
        amountOutMin: flat.amountOutMin,
        to: flat.to,
        from: flat.from,
        deadline: flat.deadline,
        slippage: flat.slippage,
    });
    const raw = await defiApi.buildSwapTransactionWithApproval(payload);
    const payloadResponse = raw;
    if (payloadResponse.data === undefined) {
        throw new Error("Unexpected API response: swap transaction response missing data");
    }
    const normalizedData = normalizeSwapResponse(payloadResponse.data);
    return { data: normalizedData };
}
async function _buildBridgeTransactionWithApproval(defiApi, _logger, request) {
    let normalized;
    if (request.bridgeSwapwithapprovalRequestPayload !== undefined) {
        const directRequest = request;
        const payload = directRequest.bridgeSwapwithapprovalRequestPayload;
        const parsedDst = parseChainIdToExpand(payload.dstChainId, "dstChainId");
        const parsedSrc = parseOptionalChainIdToExpand(payload.srcChainId, "srcChainId");
        normalized = {
            bridgeSwapwithapprovalRequestPayload: {
                ...payload,
                srcChainId: parsedSrc,
                dstChainId: parsedDst,
            },
        };
    }
    else {
        const flat = request;
        const parsedDst = parseChainIdToExpand(flat.dstChainId, "dstChainId");
        const parsedSrc = parseOptionalChainIdToExpand(flat.srcChainId, "srcChainId");
        normalized = {
            bridgeSwapwithapprovalRequestPayload: {
                ...flat,
                srcChainId: parsedSrc,
                dstChainId: parsedDst,
            },
        };
    }
    const flat = normalized.bridgeSwapwithapprovalRequestPayload;
    validateBridgeTransactionParams({
        bridgeId: flat.bridgeId,
        dstChainId: flat.dstChainId,
        srcTokenSymbol: flat.srcTokenSymbol,
        dstTokenSymbol: flat.dstTokenSymbol,
        srcTokenAddress: flat.srcTokenAddress,
        dstTokenAddress: flat.dstTokenAddress,
        amountIn: flat.amountIn,
        from: flat.from,
        to: flat.to,
    });
    const raw = await defiApi.buildBridgeTransactionWithApproval(normalized);
    const payloadResponse = raw;
    if (payloadResponse.data === undefined) {
        throw new Error("Unexpected API response: bridge transaction response missing data");
    }
    const normalizedData = normalizeBridgeResponse(payloadResponse.data);
    return { data: normalizedData };
}
async function _getUserPositions(defiApi, logger, request) {
    const raw = await defiApi.getUserPositions(request);
    return transformUserPositionsResponse(raw, logger);
}
async function _lendBorrowWithdraw(defiApi, logger, request) {
    const raw = await defiApi.lendBorrowWithdraw(request);
    return transformLendBorrowWithdrawResponse(raw, logger);
}
async function _getLendborrowPool(defiApi, logger, request) {
    const raw = await defiApi.getLendborrowPool(request);
    return transformLendborrowPoolResponse(raw, logger);
}
async function _getLendborrowPools(defiApi, logger, request) {
    const raw = await defiApi.getLendborrowPools(request);
    return transformLendborrowPoolsResponse(raw, logger);
}
async function _getApiMetadataV2(defiApi, logger, request) {
    validateApiMetadataV2Params(request);
    const raw = await defiApi.getApiMetadataV2(request);
    return transformApiMetadataV2Response(raw, logger);
}
/**
 * High-level DeFi module (public API surface).
 *
 * @remarks
 * - This class exposes developer-friendly methods that include input validation,
 *   request normalization (e.g., chain ID parsing), and response transformations.
 * - It delegates raw SDK calls to the internal `DefiApi` and returns stable, typed
 *   responses suitable for applications.
 *
 * High-level features:
 * - Swap quote aggregation and swap transaction building with approval
 * - Bridge quote aggregation and bridge transaction building with approval
 * - Bridge status retrieval
 * - Consolidated user positions (beta)
 * - Token price and metadata helpers (via `modules/common`)
 *
 * @example
 * **Swap Quote and Build**
 * ```typescript
 * const quotes = await defiModule.buildSwapQuote({
 *   chainId: "1",
 *   path: "0xC02aaA...,0xA0b869...",
 *   amountIn: "1000000000000000000"
 * });
 *
 * const tx = await defiModule.buildSwapTransactionWithApproval({
 *   dexId: "uniswap_v3",
 *   path: ["0xC02aaA...","0xA0b869..."],
 *   amountIn: "1000000000000000000",
 *   amountOutMin: "999000000",
 *   to: user,
 *   from: user,
 *   deadline: "1700000000",
 *   slippage: "1"
 * });
 * ```
 */
export class DefiModule {
    logger;
    options;
    defiApi;
    /**
     * Creates a new DefiModule instance.
     * @param options - Configuration options for the DeFi module
     * @throws {Error} If the BD API key is missing
     */
    constructor(options) {
        this.logger = options.logger || getLogger({ level: "info", label: "bd-sdk-defi-module" });
        this.options = options;
        this.defiApi = options.defiApi;
    }
    // NOTE: Non-implemented generic transaction helpers were removed from public API.
    /**
     * Builds a complete transaction object from the provided parameters.
     *
     * @remarks
     * This method constructs a complete transaction object with all necessary fields
     * filled in, ready for signing and broadcasting to the network. It validates
     * inputs and sets default values for optional parameters.
     *
     * @param params - Transaction parameters including recipient, amount, gas settings, etc.
     * @returns Promise resolving to a complete transaction object
     * @throws {Error} When SDK configuration is not initialized
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * ```typescript
     * const defiModule = new DefiModule({ bdApiKey: "your-api-key" });
     *
     * const txParams = {
     *   to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   value: "1000000000000000000", // 1 ETH in wei
     *   gasLimit: "21000",
     *   gasPrice: "20000000000" // 20 gwei
     * };
     *
     * try {
     *   const transaction = await defiModule.buildTransaction(txParams);
     *   console.log("Built transaction:", transaction);
     *   // Use the transaction for signing...
     * } catch (error) {
     *   console.error("Transaction building failed:", error.message);
     * }
     * ```
     */
    async buildTransaction(params) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        // Basic validation
        if (!params.to || typeof params.to !== "string") {
            throw new Error("Invalid 'to' address: must be a non-empty string");
        }
        // Validate address format (basic check for 0x prefix and length)
        if (!/^0x[a-fA-F0-9]{40}$/.test(params.to)) {
            throw new Error("Invalid 'to' address: must be a valid Ethereum address");
        }
        // Set default values for optional parameters.
        const transaction = {
            to: params.to,
            value: params.value || "0",
            gasLimit: params.gasLimit || "21000", // EIP-2718 base cost of a simple ETH transfer (no contract interaction)
            gasPrice: params.gasPrice || "20000000000", // 20 Gwei — conservative default for legacy gasPrice
            data: params.data || "0x",
            ...params, // Allow additional parameters to be passed through
        };
        // Validate numeric values
        try {
            if (typeof transaction.value === "string") {
                BigInt(transaction.value);
            }
            if (typeof transaction.gasLimit === "string") {
                BigInt(transaction.gasLimit);
            }
            if (typeof transaction.gasPrice === "string") {
                BigInt(transaction.gasPrice);
            }
        }
        catch (_error) {
            throw new Error("Invalid numeric value in transaction parameters");
        }
        this.logger.debug("Built transaction", transaction);
        return transaction;
    }
    /**
     * Retrieves transaction history for a specific address (mock implementation).
     *
     * @deprecated
     * @remarks not implemented, returns mocks
     */
    async getTransactions(address) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        // Mapping logic not yet implemented — returns mock data (see @deprecated above)
        this.logger.debug("getTransactions", { address });
        this.logger.warn("get transactions returning mock data");
        return _getTransactions(address);
    }
    /**
     * Retrieves the status of a bridge transaction using its transaction hash.
     *
     * @remarks
     * This method fetches detailed information about a cross-chain bridge transaction
     * including its current status, source and destination details, and transaction metadata.
     * It's useful for monitoring the progress of bridge transfers and verifying completion.
     *
     * **Features:**
     * - Real-time transaction status tracking
     * - Cross-chain transaction details
     * - Comprehensive transaction metadata
     * - Bridge-specific information
     *
     * **Required Parameters:**
     * - `transactionHash`: The hash of the bridge transaction
     * - `bridgeId`: The identifier of the bridge used
     *
     * **Optional Parameters:**
     * - `srcChainId`: Source chain identifier for additional context
     * - `srcChainSymbol`: Source chain symbol for additional context
     *
     * @param request - The bridge status request parameters
     * @returns Promise resolving to bridge status data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * ```typescript
     * const status = await defiModule.getBridgeStatus({
     *   transactionHash: "0xabc...",
     *   bridgeId: "stargate"
     * });
     * ```
     *
     * @example
     * **Basic Status Query**
     * ```typescript
     * const status = await defiModule.getBridgeStatus({
     *   transactionHash: "0x1234567890abcdef...",
     *   bridgeId: "stargate"
     * });
     *
     * console.log("Transaction status:", status.data.status);
     * console.log("Source chain:", status.data.srcChainId);
     * console.log("Destination chain:", status.data.dstChainId);
     * console.log("Amount:", status.data.amount);
     * ```
     *
     * @example
     * **Status Query with Chain Context**
     * ```typescript
     * const status = await defiModule.getBridgeStatus({
     *   transactionHash: "0x1234567890abcdef...",
     *   bridgeId: "stargate",
     *   srcChainId: "1",
     *   srcChainSymbol: "ETH"
     * });
     *
     * console.log("Bridge transaction details:", status.data);
     * ```
     *
     * @see {@link IGetBridgeStatusResponse} for response structure
     */
    async getBridgeStatus(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.getBridgeStatus", this.logger, _getBridgeStatus(this.defiApi, this.logger, request));
    }
    /**
     * Creates a lendborrow deposit transaction using the DeFi API.
     *
     * @remarks
     * This method creates a transaction for depositing assets into lending and borrowing
     * protocols through Blockdaemon's DeFi infrastructure. It supports various protocols
     * including Compound, Aave, and Morpho with customizable gas settings.
     *
     * @param request - The lendborrow deposit request parameters
     * @returns Promise resolving to deposit transaction data or structured error response
     *
     * @example
     * ```typescript
     * const deposit = await defiModule.lendBorrowDeposit({
     *   lendborrowDepositRequestPayload: {
     *     lendborrowId: "1100", // Compound V2 on Ethereum
     *     asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *     amount: "1000000", // 1 USDC (6 decimals)
     *     onBehalfOf: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *     from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     *   }
     * });
     * ```
     *
     * @public
     */
    async lendBorrowDeposit(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.lendBorrowDeposit", this.logger, _lendBorrowDeposit(this.defiApi, this.logger, request));
    }
    /**
     * Builds swap quotes from multiple DEXs for a specified token path.
     *
     * @remarks
     * Aggregates swap quotes from various DEXs for a given token swap path,
     * allowing users to compare rates and identify the most favorable trading options.
     *
     * @param request - The swap quote request parameters
     * @returns Promise resolving to aggregated quote data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * ```typescript
     * const quotes = await defiModule.buildSwapQuote({
     *   chainId: "1",
     *   path: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2,0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
     *   amountIn: "1000000000000000000" // 1 WETH in wei
     * });
     *
     * console.log(`Found ${quotes.data.length} DEX quotes`);
     * ```
     *
     * @see {@link IBuildSwapQuoteResponse} for response structure
     */
    async buildSwapQuote(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.buildSwapQuote", this.logger, _buildSwapQuote(this.defiApi, this.logger, request));
    }
    /**
     * Builds bridge quotes from multiple bridges for cross-chain token transfers.
     *
     * @remarks
     * Aggregates bridge quotes from various cross-chain bridges for a given token pair,
     * allowing users to compare rates and identify the most favorable bridging option.
     * Tokens must be specified by either (srcTokenSymbol + dstTokenSymbol) or (srcTokenAddress + dstTokenAddress), not both.
     *
     * @param request - The bridge quote request parameters
     * @returns Promise resolving to aggregated bridge quote data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * **Using token symbols**
     * ```typescript
     * const bridgeQuotes = await defiModule.buildBridgeQuote({
     *   dstChainId: "137",
     *   srcTokenSymbol: "USDC",
     *   dstTokenSymbol: "USDC",
     *   amountIn: "1000000" // 1 USDC (6 decimals)
     * });
     * ```
     *
     * @example
     * **Using token addresses**
     * ```typescript
     * const bridgeQuotes = await defiModule.buildBridgeQuote({
     *   dstChainId: "137",
     *   srcTokenAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC on Ethereum
     *   dstTokenAddress: "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174", // USDC on Polygon
     *   amountIn: "1000000"
     * });
     * ```
     *
     * @see {@link IBuildBridgeQuoteResponse} for response structure
     */
    async buildBridgeQuote(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.buildBridgeQuote", this.logger, _buildBridgeQuote(this.defiApi, this.logger, request));
    }
    /**
     * Builds swap transaction with approval for a specified token pair.
     *
     * @param request - The swap transaction request parameters
     * @returns Promise resolving to transaction data or structured error response
     *
     * @see {@link ISwapWithApprovalRequest} for request parameters
     * @see {@link IBuildSwapTransactionWithApprovalResponse} for response structure
     *
     * @example
     * ```typescript
     * const swapTransaction = await defiModule.buildSwapTransactionWithApproval({
     *   dexId: "uniswap_v3",
     *   path: ["0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48"],
     *   amountIn: "1000000000000000000", // 1 WETH
     *   amountOutMin: "3800000000", // Minimum 3800 USDC
     *   to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   deadline: "1700000000",
     *   slippage: "1"
     * });
     *
     * console.log("Transaction data:", swapTransaction.data);
     * ```
     */
    async buildSwapTransactionWithApproval(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.buildSwapTransactionWithApproval", this.logger, _buildSwapTransactionWithApproval(this.defiApi, this.logger, request));
    }
    /**
     * Builds bridge transaction with approval for cross-chain token transfers.
     *
     * @remarks
     * Tokens must be specified by either (srcTokenSymbol + dstTokenSymbol) or (srcTokenAddress + dstTokenAddress), not both.
     *
     * @param request - The bridge transaction request parameters
     * @returns Promise resolving to transaction data or structured error response
     *
     * @see {@link IBridgeWithApprovalRequest} for request parameters
     * @see {@link IBuildBridgeTransactionWithApprovalResponse} for response structure
     *
     * @example
     * **Flattened payload (token symbols)**
     * ```typescript
     * const bridgeTransaction = await defiModule.buildBridgeTransactionWithApproval({
     *   bridgeId: "600",
     *   srcChainId: "1",
     *   dstChainId: "137",
     *   srcTokenSymbol: "USDC",
     *   dstTokenSymbol: "USDC",
     *   amountIn: "1000000",
     *   amountOutMin: "999000",
     *   from: "0x...",
     *   to: "0x...",
     *   slippage: "1"
     * });
     * ```
     *
     * @example
     * **Wrapped payload**
     * ```typescript
     * const bridgeTransaction = await defiModule.buildBridgeTransactionWithApproval({
     *   bridgeSwapwithapprovalRequestPayload: {
     *     bridgeId: "600",
     *     srcChainId: "1",
     *     dstChainId: "137",
     *     srcTokenSymbol: "USDC",
     *     dstTokenSymbol: "USDC",
     *     amountIn: "1000000",
     *     from: "0x...",
     *     to: "0x...",
     *     slippage: "1"
     *   }
     * });
     * ```
     */
    async buildBridgeTransactionWithApproval(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.buildBridgeTransactionWithApproval", this.logger, _buildBridgeTransactionWithApproval(this.defiApi, this.logger, request));
    }
    /**
     * Retrieves comprehensive DeFi positions for a specified user address.
     *
     * @remarks
     * This method fetches all DeFi positions associated with a user's address across
     * various protocols including lending, borrowing, liquidity provision, and staking.
     * The positions include real-time USD valuations and detailed position metadata.
     *
     * Under the hood, this calls `DefiApi.getUserPositions`, which forwards to the
     * Consolidated DeFi SDK user positions endpoint.
     *
     * @param request - The user positions query parameters
     * @returns Promise resolving to user positions data or structured error response
     * @throws {Error} When SDK configuration is not initialized
     * @throws {Error} When the address parameter is missing or invalid
     *
     * @example
     * ```typescript
     * const defiModule = new DefiModule({ bdApiKey: "your-api-key" });
     *
     * const positions = await defiModule.getUserPositions({
     *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     * });
     *
     * console.log(`Total value: $${positions.data.totalValueUSD}`);
     * console.log(`Number of positions: ${positions.data.positions.length}`);
     *
     * positions.data.positions.forEach(position => {
     *   console.log(`${position.protocol} ${position.type}: $${position.valueUSD}`);
     *   });
     * }
     * ```
     *
     * @see {@link IGetUserPositionsResponse} for response structure
     */
    async getUserPositions(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        validateUserPositionsParams({
            address: request.address,
            lendborrowId: request.lendborrowId,
            vsCurrencies: request.vsCurrencies,
            marketId: request.marketId,
            vaultAddress: request.vaultAddress,
        });
        return executeDefiOperation("DefiModule.getUserPositions", this.logger, _getUserPositions(this.defiApi, this.logger, request));
    }
    /**
     * Processes the withdrawal of deposited assets from the lending protocol.
     *
     * @remarks
     * Allows users to withdraw their deposited assets from various lending protocols
     * such as Aave, Compound, and others. Supports multiple chains and protocols.
     *
     * @param request - The withdrawal request parameters
     * @returns Promise resolving to withdrawal transaction data or structured error response
     * @throws {Error} When SDK configuration is not initialized
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * ```typescript
     * const withdrawal = await defiModule.lendBorrowWithdraw({
     *   lendborrowWithdrawRequestPayload: {
     *     lendborrowId: "1000", // Aave V2 on Ethereum
     *     asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *     amount: "1000000", // 1 USDC (6 decimals)
     *     to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6"
     *   }
     * });
     *
     * console.log("Transaction data:", withdrawal.data);
     * ```
     *
     * @see {@link ILendBorrowWithdrawResponse} for response structure
     */
    async lendBorrowWithdraw(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        const { lendborrowWithdrawRequestPayload } = request;
        if (!lendborrowWithdrawRequestPayload)
            throw new Error("Withdrawal request payload is required");
        const { lendborrowId, asset, amount, to } = lendborrowWithdrawRequestPayload;
        validateLendborrowWithdrawParams({ lendborrowId, asset, amount, to });
        return executeDefiOperation("DefiModule.lendBorrowWithdraw", this.logger, _lendBorrowWithdraw(this.defiApi, this.logger, { lendborrowWithdrawRequestPayload }));
    }
    /**
     * Retrieves current lending and borrowing rates (APYs) for a specified asset within a pool.
     *
     * @remarks
     * This method fetches key metrics such as variable/stable borrow and supply rates,
     * loan-to-value ratios, available liquidity, and reserve size. This data is essential
     * for evaluating the attractiveness and risk of entering or exiting a market.
     *
     * **Features:**
     * - Real-time APY data for lending and borrowing
     * - Variable and stable rate information
     * - Loan-to-value ratios and risk metrics
     * - Available liquidity and reserve size data
     * - Multi-protocol support (Aave, Compound, etc.)
     *
     * **Supported Protocols:**
     * - Aave V2 (lendborrowId: "1000") - Ethereum
     * - Aave V3 (lendborrowId: "1200") - Ethereum
     * - Compound V2 (lendborrowId: "1100") - Ethereum
     * - Compound V3 (lendborrowId: "1300") - Ethereum
     * - Morpho (lendborrowId: "1400") - Ethereum
     * - And many more across different chains
     *
     * @param request - The lendborrow pool request parameters
     * @returns Promise resolving to pool data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * **Basic Pool Query**
     * ```typescript
     * const pool = await defiModule.getLendborrowPool({
     *   asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" // USDC
     * });
     *
     * console.log("Pool data:", pool.data);
     * ```
     *
     * @example
     * **Pool Query with Protocol and Market**
     * ```typescript
     * const pool = await defiModule.getLendborrowPool({
     *   asset: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *   lendborrowId: "1000", // Aave V2
     *   marketId: "usdc",
     *   type: "Approval"
     * });
     * ```
     *
     * @see {@link IGetLendborrowPoolResponse} for response structure
     */
    async getLendborrowPool(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        const { asset, lendborrowId, marketId, vaultAddress } = request;
        validateLendborrowPoolParams({
            asset,
            lendborrowId,
            marketId,
            vaultAddress,
        });
        return executeDefiOperation("DefiModule.getLendborrowPool", this.logger, _getLendborrowPool(this.defiApi, this.logger, request));
    }
    /**
     * Retrieves comprehensive overview of supply and borrow APYs across multiple assets in lending pools.
     *
     * @remarks
     * This method fetches aggregated data such as interest rates, liquidity, reserve sizes,
     * and token-specific details (like symbols and addresses) to help compare opportunities
     * across different markets. It provides a comprehensive view of multiple lending pools
     * in a single request.
     *
     * **Features:**
     * - Multi-asset pool data aggregation
     * - Supply and borrow APY comparison
     * - Interest rates and liquidity information
     * - Reserve sizes and token details
     * - Multi-protocol support (Aave, Compound, etc.)
     *
     * **Supported Protocols:**
     * - Aave V2 (lendborrowId: "1000") - Ethereum
     * - Aave V3 (lendborrowId: "1200") - Ethereum
     * - Compound V2 (lendborrowId: "1100") - Ethereum
     * - Compound V3 (lendborrowId: "1300") - Ethereum
     * - Morpho (lendborrowId: "1400") - Ethereum
     * - And many more across different chains
     *
     * @param request - The lendborrow pools request parameters
     * @returns Promise resolving to pools data or structured error response
     * @throws {Error} When required parameters are missing or invalid
     *
     * @example
     * **Basic Pools Query**
     * ```typescript
     * const pools = await defiModule.getLendborrowPools({
     *   assets: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48,0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2" // USDC,WETH
     * });
     *
     * console.log("Pools data:", pools.data);
     * ```
     *
     * @example
     * **Pools Query with Protocol and Markets**
     * ```typescript
     * const pools = await defiModule.getLendborrowPools({
     *   assets: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48,0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
     *   lendborrowId: "1000", // Aave V2
     *   markets: "usdc,weth"
     * });
     * ```
     *
     * @see {@link IGetLendborrowPoolsResponse} for response structure
     */
    async getLendborrowPools(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        const { assets, lendborrowId, address, markets, vaults } = request;
        validateLendborrowPoolsParams({
            assets,
            lendborrowId,
            address,
            markets,
            vaults,
        });
        return executeDefiOperation("DefiModule.getLendborrowPools", this.logger, _getLendborrowPools(this.defiApi, this.logger, request));
    }
    /**
     * Retrieves API metadata for DeFi protocols and services.
     *
     * @remarks
     * Fetches comprehensive metadata about supported DeFi protocols including names,
     * logos, and chain support information. Essential for building dynamic UIs that
     * display protocol information.
     *
     * @param request - Request parameters with category and endpoint filters
     * @returns Promise resolving to protocol metadata or error
     *
     * @example
     * ```typescript
     * const defi = sdk.defi;
     * const metadata = await defi.getApiMetadataV2({
     *   category: "lendborrow",
     *   endpoint: "getpool"
     * });
     *
     * metadata.data.forEach(protocol => {
     *   console.log(`${protocol.protocolName}: ${protocol.protocolLogo}`);
     *   protocol.supportedChains?.forEach(chain => {
     *     console.log(`  - ${chain.chainName} (${chain.chainId})`);
     *   });
     * });
     * ```
     */
    async getApiMetadataV2(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeDefiOperation("DefiModule.getApiMetadataV2", this.logger, _getApiMetadataV2(this.defiApi, this.logger, request));
    }
}
//# sourceMappingURL=data:application/json;base64,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